import { supabase } from '~/services/supabase';
import type { IFormSchema, IIntentClassification } from '~/types';
import { ZFormSchema, validateSchemaConsistency } from '~/types';

export type AIMessage = {
  role: 'user' | 'assistant' | 'system';
  content: string;
};

export type AIStep =
  | 'context_gathering'
  | 'followup'
  | 'schema_generation'
  | 'validation'
  | 'intent_routing'
  | 'schema_editing';

/**
 * Invoke the ai-form-builder Supabase Edge Function.
 * All AI keys (OpenAI) live server-side — never in the browser.
 */
async function invokeEdgeFunction<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('ai-form-builder', {
    body,
  });

  if (error) {
    throw new Error(error.message || 'Edge function invocation failed');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data as T;
}

/**
 * Context Gathering Step
 */
export async function gatherContext(messages: AIMessage[]): Promise<string> {
  const result = await invokeEdgeFunction<{ message: string }>({
    action: 'gatherContext',
    messages,
  });
  return result.message;
}

/**
 * Follow-up Questions Step
 */
export async function askFollowUp(messages: AIMessage[], confidence: number): Promise<string> {
  const result = await invokeEdgeFunction<{ message: string }>({
    action: 'askFollowUp',
    messages,
    confidence,
  });
  return result.message;
}

/**
 * Intent Classification Step
 */
export async function classifyIntent(
  messages: AIMessage[],
  existingSchema: IFormSchema | null,
): Promise<IIntentClassification> {
  return invokeEdgeFunction<IIntentClassification>({
    action: 'classifyIntent',
    messages,
    existingSchema,
  });
}

/**
 * Schema Edit Step
 */
export async function editSchema(messages: AIMessage[], existingSchema: IFormSchema): Promise<IFormSchema> {
  const result = await invokeEdgeFunction<{ schema: IFormSchema }>({
    action: 'editSchema',
    messages,
    existingSchema,
  });
  return result.schema;
}

/**
 * Schema Generation Step
 */
export async function generateSchema(messages: AIMessage[]): Promise<IFormSchema> {
  const result = await invokeEdgeFunction<{ schema: IFormSchema }>({
    action: 'generateSchema',
    messages,
  });
  return result.schema;
}

/**
 * Schema Validation Step - Using Zod schema validation (runs client-side)
 */
export function validateSchema(schema: IFormSchema): { valid: boolean; errors?: string[] } {
  try {
    if (!schema.type || schema.type !== 'object') {
      return { valid: false, errors: ['Schema must have type: "object"'] };
    }
    if (!schema.properties || typeof schema.properties !== 'object') {
      return { valid: false, errors: ['Schema must have properties object'] };
    }
    ZFormSchema.parse(schema);

    // Cross-field consistency checks (layout grid coverage, steps coverage)
    const consistency = validateSchemaConsistency(schema);
    if (!consistency.valid) {
      return { valid: false, errors: consistency.errors };
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      errors: [error instanceof Error ? error.message : 'Invalid schema structure'],
    };
  }
}

/**
 * Complete AI Form Builder Workflow
 * Delegates all AI calls to the edge function.
 */
export async function runAIFormBuilder(
  userInput: string,
  conversationHistory: AIMessage[],
  onMessage: (message: string, step: AIStep) => void,
  existingSchema?: IFormSchema | null,
): Promise<IFormSchema> {
  onMessage('', 'intent_routing');

  const result = await invokeEdgeFunction<{
    schema?: IFormSchema;
    clarification?: string;
    error?: string;
  }>({
    action: 'runAIFormBuilder',
    userInput,
    conversationHistory,
    existingSchema,
  });

  if (result.error) {
    throw new Error(result.error);
  }

  if (result.clarification) {
    throw new Error(`NEED_CLARIFICATION: ${result.clarification}`);
  }

  if (!result.schema) {
    throw new Error('No schema returned from AI');
  }

  // Validate the returned schema client-side and retry via AI if invalid
  let schema = result.schema;
  const maxRetries = 2;
  for (let i = 0; i <= maxRetries; i++) {
    const validation = validateSchema(schema);
    if (validation.valid) {
      return schema;
    }
    if (i === maxRetries) {
      throw new Error(`Schema validation failed: ${validation.errors?.join(', ')}`);
    }
    onMessage('', 'validation');
    const fixResult = await invokeEdgeFunction<{ schema: IFormSchema }>({
      action: 'fixSchemaWithAI',
      schema,
      errors: validation.errors,
      conversationHistory: [...conversationHistory, { role: 'user', content: userInput }],
    });
    schema = fixResult.schema;
  }

  return schema;
}
